import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, TextField, MenuItem, Chip,
  Stack, Grid, CircularProgress, Checkbox, FormControlLabel,
  Card, CardContent, Divider, Tooltip, ToggleButtonGroup, ToggleButton,
  Collapse, IconButton
} from '@mui/material';
import {
  Delete as DeleteIcon, FilterList as FilterIcon,
  MeetingRoom as RoomIcon, CalendarMonth as CalendarIcon,
  AccessTime as TimeIcon, Groups as GroupsIcon,
  Person as TeacherIcon, ViewList as TableListIcon,
  GridView as GridViewIcon, DeleteSweep as DeleteSweepIcon,
  CheckCircle as CheckCircleIcon, Close as CloseIcon
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable, { ColumnConfig } from '../../Commun/components/GenericTable';
import { BlobticDataManager } from '../../Commun/services/blobticDataManager';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { academyStore } from '../services/academyStore';
import { planningTheoriqueApi } from '../services/planningTheoriqueApi';
import { SessionDeletionItem } from '../types/planningTheorique';

const formatToday = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatNextMonth = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const PlanningDeletionPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useLanguage();
  const [, setTick] = useState(0);

  const [viewMode, setViewMode] = useDefaultViewMode();

  // Résultat de suppression
  const [deletionResult, setDeletionResult] = useState<{
    deletedCount: number;
    timestamp: string;
  } | null>(null);

  // Filtres
  const [startDate, setStartDate] = useState<string>(formatToday());
  const [endDate, setEndDate] = useState<string>(formatNextMonth());
  const [selectedRoomIds, setSelectedRoomIds] = useState<number[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [selectedTeacherIds, setSelectedTeacherIds] = useState<number[]>([]);

  // Données & Sélection
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sessions, setSessions] = useState<SessionDeletionItem[]>([]);
  const [selectedSessionIds, setSelectedSessionIds] = useState<number[]>([]);

  useEffect(() => {
    academyStore.ensureRoomsLoaded();
    academyStore.ensureGroupsLoaded();
    academyStore.ensureTeachersLoaded();
    return academyStore.subscribe(() => setTick(tick => tick + 1));
  }, []);

  const rooms = academyStore.getRooms();
  const groups = academyStore.getGroups();
  const teachers = academyStore.getTeachers();

  // Chargement des séances
  const handleLoad = async () => {
    if (!startDate || !endDate) {
      blobticMessage.warning(t('commun.warning'), t('academy.planning_theorique.validation.dates_required'));
      return;
    }
    if (endDate < startDate) {
      blobticMessage.warning(t('commun.warning'), t('academy.planning_theorique.validation.invalid_date_range'));
      return;
    }

    setLoading(true);
    try {
      const data = await planningTheoriqueApi.getSessionsForDeletion({
        startDate,
        endDate,
        roomIds: selectedRoomIds.length > 0 ? selectedRoomIds : undefined,
        groupIds: selectedGroupIds.length > 0 ? selectedGroupIds : undefined,
        teacherIds: selectedTeacherIds.length > 0 ? selectedTeacherIds : undefined
      });

      setSessions(data);
      setSelectedSessionIds([]);
    } catch (err: any) {
      blobticMessage.error(t('commun.error'), err?.message || 'Erreur lors du chargement des séances.');
    } finally {
      setLoading(false);
    }
  };

  // Sélection globale ou unitaire
  const handleToggleSelectAll = () => {
    if (selectedSessionIds.length === sessions.length) {
      setSelectedSessionIds([]);
    } else {
      setSelectedSessionIds(sessions.map(s => s.id));
    }
  };

  const handleToggleSession = (id: number) => {
    setSelectedSessionIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Suppression par lot
  const handleDeleteSelected = async () => {
    if (selectedSessionIds.length === 0) {
      blobticMessage.warning(t('commun.warning'), t('academy.planning_theorique.select_at_least_one_session'));
      return;
    }

    const confirmed = await blobticMessage.confirm({
      title: t('commun.confirmation'),
      message: t('academy.planning_theorique.del_confirm', selectedSessionIds.length) ||
        `Êtes-vous sûr de vouloir supprimer définitivement les ${selectedSessionIds.length} séance(s) sélectionnée(s) ? Cette action est irréversible.`,
      severity: 'warning'
    });

    if (!confirmed) return;

    setDeleting(true);
    try {
      const res = await planningTheoriqueApi.deleteSessionsBatch(selectedSessionIds);

      // Synchroniser le cache IndexedDB des séances
      await BlobticDataManager.removeStores(['academy_session']);
      academyStore.notify(['academy_session']);

      setDeletionResult({
        deletedCount: res.deletedCount,
        timestamp: new Date().toLocaleTimeString()
      });

      const successMsg = res.message || t('academy.planning_theorique.deletion_success_msg', res.deletedCount);

      blobticMessage.alert({
        title: t('commun.success'),
        message: successMsg,
        severity: 'success'
      });

      // Mettre à jour l'état local
      const remaining = sessions.filter(s => !selectedSessionIds.includes(s.id));
      setSessions(remaining);
      setSelectedSessionIds([]);
    } catch (err: any) {
      blobticMessage.error(t('commun.error'), err?.message || 'Erreur lors de la suppression.');
    } finally {
      setDeleting(false);
    }
  };

  const formatSessionDate = (dtStr: string): string => {
    if (!dtStr) return '';
    try {
      const parts = dtStr.split('T');
      return parts[0];
    } catch {
      return dtStr;
    }
  };

  const formatSessionTime = (dtStr: string): string => {
    if (!dtStr) return '';
    try {
      if (dtStr.includes('T')) {
        return dtStr.split('T')[1].substring(0, 5);
      }
      return dtStr.substring(0, 5);
    } catch {
      return '';
    }
  };

  // Colonnes pour GenericTable
  const tableColumns: ColumnConfig[] = [
    {
      id: 'selection',
      label: '',
      render: (row: SessionDeletionItem) => (
        <Checkbox
          size="small"
          checked={selectedSessionIds.includes(row.id)}
          onChange={() => handleToggleSession(row.id)}
        />
      )
    },
    {
      id: 'date',
      label: t('academy.planning_theorique.date'),
      render: (row: SessionDeletionItem) => (
        <Typography variant="body2" fontWeight={600}>
          {formatSessionDate(row.start_hour)}
        </Typography>
      )
    },
    {
      id: 'time',
      label: t('academy.planning_theorique.hours'),
      render: (row: SessionDeletionItem) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <TimeIcon fontSize="small" color="primary" />
          <Typography variant="body2" fontWeight={600} color="primary.main">
            {formatSessionTime(row.start_hour)} - {formatSessionTime(row.end_hour)}
          </Typography>
        </Stack>
      )
    },
    {
      id: 'room',
      label: t('academy.planning_theorique.room'),
      render: (row: SessionDeletionItem) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <RoomIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {row.room_number || `Salle #${row.arm_idf}`}
          </Typography>
        </Stack>
      )
    },
    {
      id: 'group',
      label: t('academy.planning_theorique.group'),
      render: (row: SessionDeletionItem) => (
        <Typography variant="body2">{row.grp_name || '—'}</Typography>
      )
    },
    {
      id: 'teacher',
      label: t('academy.planning_theorique.teacher'),
      render: (row: SessionDeletionItem) => (
        <Typography variant="body2">{row.teacher_name || '—'}</Typography>
      )
    },
    {
      id: 'status',
      label: t('academy.planning_theorique.status'),
      render: (row: SessionDeletionItem) => (
        <Chip
          label={row.status || 'Scheduled'}
          size="small"
          color={row.status === 'Completed' ? 'success' : row.status === 'Cancelled' ? 'error' : 'default'}
        />
      )
    }
  ];

  return (
    <GenericPage
      title={t('academy.planning_theorique.deletion_title')}
      icon={<DeleteSweepIcon />}
      footerEnabled={false}
      additionalActions={
        <Stack direction="row" spacing={1.5} alignItems="center">
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, val) => { if (val) setViewMode(val); }}
            size="small"
          >
            <ToggleButton value="grid">
              <Tooltip title={t('commun.view_grid')}>
                <GridViewIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="table">
              <Tooltip title={t('commun.view_table')}>
                <TableListIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="contained"
            color="error"
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            disabled={deleting || selectedSessionIds.length === 0}
            onClick={handleDeleteSelected}
            sx={{ borderRadius: 2, px: 3 }}
          >
            {t('academy.planning_theorique.delete_button')} ({selectedSessionIds.length})
          </Button>
        </Stack>
      }
    >
      <Box sx={{ pb: 4 }}>
        {/* ── Bannière Résultat de Suppression ── */}
        <Collapse in={!!deletionResult}>
          {deletionResult && (
            <Paper
              elevation={3}
              sx={{
                p: 2.5,
                mb: 3,
                borderRadius: 3,
                bgcolor: isDark ? 'rgba(211, 47, 47, 0.18)' : '#ffebee',
                border: '1.5px solid',
                borderColor: isDark ? 'error.dark' : 'error.main',
                position: 'relative'
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ p: 1.5, borderRadius: '50%', bgcolor: 'error.main', color: '#fff', display: 'flex' }}>
                    <CheckCircleIcon sx={{ fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="error.dark">
                      {t('academy.planning_theorique.deletion_success_title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('academy.planning_theorique.deletion_success_subtitle', deletionResult.deletedCount)}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                      <Chip
                        icon={<CheckCircleIcon fontSize="small" />}
                        label={`${deletionResult.deletedCount} ${t('academy.planning_theorique.sessions_deleted')}`}
                        color="error"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                      <Chip
                        label={`${t('common.date_time') || 'Heure'} : ${deletionResult.timestamp}`}
                        variant="outlined"
                        size="small"
                      />
                    </Stack>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CalendarIcon />}
                    onClick={() => navigate('/Academy/scheduler')}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  >
                    {t('academy.planning_theorique.view_scheduler')}
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => setDeletionResult(null)}
                    sx={{ color: 'text.secondary' }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          )}
        </Collapse>

        {/* ── Filtres de recherche ── */}
        <Paper elevation={2} sx={{ p: 2.5, mb: 3, borderRadius: 3, bgcolor: 'background.paper' }}>
          <Grid container spacing={2} alignItems="center">
            {/* 1. Date Début */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                type="date"
                fullWidth
                size="small"
                required
                label={t('academy.planning_theorique.start_date')}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* 2. Date Fin */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                type="date"
                fullWidth
                size="small"
                required
                label={t('academy.planning_theorique.end_date')}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* 3. Salles Multi-Select */}
            <Grid item xs={12} sm={4} md={2.5}>
              <TextField
                select
                fullWidth
                size="small"
                SelectProps={{
                  multiple: true,
                  renderValue: (selected: any) => {
                    const sel = selected as number[];
                    if (sel.length === 0) return <em>{t('academy.planning_theorique.all_rooms')}</em>;
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {sel.map(id => {
                          const r = rooms.find(x => x.id === id);
                          return <Chip key={id} label={r?.room_number || `#${id}`} size="small" />;
                        })}
                      </Box>
                    );
                  }
                }}
                label={t('academy.planning_theorique.room_filter')}
                value={selectedRoomIds}
                onChange={(e) => setSelectedRoomIds(typeof e.target.value === 'string' ? [] : (e.target.value as number[]))}
              >
                {rooms.map(r => (
                  <MenuItem key={r.id} value={r.id}>
                    <Checkbox checked={selectedRoomIds.includes(r.id)} size="small" />
                    {r.room_number} {r.floor ? `(${r.floor})` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* 4. Groupes Multi-Select */}
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                SelectProps={{
                  multiple: true,
                  renderValue: (selected: any) => {
                    const sel = selected as number[];
                    if (sel.length === 0) return <em>{t('academy.planning_theorique.all_groups')}</em>;
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {sel.map(id => {
                          const g = groups.find(x => x.id === id);
                          return <Chip key={id} label={g?.name || `#${id}`} size="small" />;
                        })}
                      </Box>
                    );
                  }
                }}
                label={t('academy.planning_theorique.group_filter')}
                value={selectedGroupIds}
                onChange={(e) => setSelectedGroupIds(typeof e.target.value === 'string' ? [] : (e.target.value as number[]))}
              >
                {groups.map(g => (
                  <MenuItem key={g.id} value={g.id}>
                    <Checkbox checked={selectedGroupIds.includes(g.id)} size="small" />
                    {g.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* 5. Enseignants Multi-Select */}
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                SelectProps={{
                  multiple: true,
                  renderValue: (selected: any) => {
                    const sel = selected as number[];
                    if (sel.length === 0) return <em>{t('academy.planning_theorique.all_teachers')}</em>;
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {sel.map(id => {
                          const tch = teachers.find(x => x.id === id);
                          return <Chip key={id} label={tch?.full_name || `#${id}`} size="small" />;
                        })}
                      </Box>
                    );
                  }
                }}
                label={t('academy.planning_theorique.teacher_filter')}
                value={selectedTeacherIds}
                onChange={(e) => setSelectedTeacherIds(typeof e.target.value === 'string' ? [] : (e.target.value as number[]))}
              >
                {teachers.map(tch => (
                  <MenuItem key={tch.id} value={tch.id}>
                    <Checkbox checked={selectedTeacherIds.includes(tch.id)} size="small" />
                    {tch.full_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* 6. Bouton Charger */}
            <Grid item xs={12} md={1.5}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <FilterIcon />}
                disabled={loading}
                onClick={handleLoad}
                sx={{ borderRadius: 2, height: 40 }}
              >
                {t('academy.planning_theorique.load_btn')}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* ── Barre de sélection ── */}
        {sessions.length > 0 && (
          <Paper
            elevation={1}
            sx={{
              p: 1.5,
              mb: 2.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.error.main, isDark ? 0.15 : 0.05),
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={selectedSessionIds.length === sessions.length && sessions.length > 0}
                  indeterminate={selectedSessionIds.length > 0 && selectedSessionIds.length < sessions.length}
                  onChange={handleToggleSelectAll}
                />
              }
              label={
                <Typography variant="body2" fontWeight={600}>
                  {selectedSessionIds.length === sessions.length
                    ? t('academy.planning_theorique.all_selected')
                    : `${selectedSessionIds.length} / ${sessions.length} ${t('academy.planning_theorique.sessions_selected')}`}
                </Typography>
              }
            />
          </Paper>
        )}

        {/* ── Affichage Grille vs Tableau ── */}
        {viewMode === 'grid' ? (
          <Grid container spacing={2}>
            {sessions.map((item) => {
              const isSelected = selectedSessionIds.includes(item.id);

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                  <Card
                    elevation={isSelected ? 3 : 1}
                    sx={{
                      borderRadius: 3,
                      cursor: 'pointer',
                      border: `2px solid ${isSelected ? theme.palette.error.main : 'transparent'}`,
                      bgcolor: isSelected ? alpha(theme.palette.error.main, isDark ? 0.12 : 0.04) : 'background.paper',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 3
                      }
                    }}
                    onClick={() => handleToggleSession(item.id)}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Checkbox
                            size="small"
                            color="error"
                            checked={isSelected}
                            onChange={() => handleToggleSession(item.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Typography variant="body2" fontWeight={700}>
                            {formatSessionDate(item.start_hour)}
                          </Typography>
                        </Stack>

                        <Chip
                          label={item.status || 'Scheduled'}
                          size="small"
                          color={item.status === 'Completed' ? 'success' : item.status === 'Cancelled' ? 'error' : 'default'}
                          sx={{ fontSize: '0.7rem', height: 22 }}
                        />
                      </Stack>

                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 4, mb: 1 }}>
                        <TimeIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                        <Typography variant="body2" fontWeight={700} color="primary.main">
                          {formatSessionTime(item.start_hour)} - {formatSessionTime(item.end_hour)}
                        </Typography>
                      </Stack>

                      <Divider sx={{ my: 1 }} />

                      <Stack spacing={0.5} sx={{ ml: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <RoomIcon sx={{ fontSize: 14, color: 'action.active' }} />
                          <Typography variant="caption">
                            {item.room_number || `Salle #${item.arm_idf}`}
                          </Typography>
                        </Stack>

                        {item.grp_name && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <GroupsIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
                            <Typography variant="caption" noWrap>
                              {item.grp_name}
                            </Typography>
                          </Stack>
                        )}

                        {item.teacher_name && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <TeacherIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {item.teacher_name}
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}

            {sessions.length === 0 && !loading && (
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                  <CalendarIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1.5 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t('academy.planning_theorique.no_sessions_found')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('academy.planning_theorique.no_sessions_found_desc')}
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        ) : (
          <GenericTable
            columns={tableColumns}
            data={sessions}
            loading={loading}
            searchPlaceholder={t('academy.planning_theorique.search_placeholder')}
          />
        )}
      </Box>
    </GenericPage>
  );
};

export default PlanningDeletionPage;
